// Day-8 ProjectPicker：底部弹出的"选项目 + 选会话"面板
//
// 流程：
//   1. 用户点输入框上方的 "📁 项目: <selected_path>" 进入这个面板
//   2. 拉 list_dirs(selectedAgentId) → 显示 dir 列表
//   3. 用户点一个 dir → 选中并自动拉 list_sessions(cwd)
//   4. 显示 sessions 列表 + "继续最近一次" + "新对话" 选项
//   5. 用户选完点 ✓ → 关闭面板
//
// 状态：
//   - 选中的 cwd 存到 selectedCwd（global signal）
//   - 选中的续接策略：selectedResumeId（具体 session id）/ continueRecent（true 表示用 -c）/ 都不选（开新对话）
import { useEffect, useState } from "preact/hooks";
import {
  selectedAgentId,
  selectedCwd,
  selectedResumeId,
  continueRecent,
  dirsByAgent,
  sessionsByCwd,
} from "../state/session";
import type { CliRPC, DirEntry, SessionEntry } from "../transport/cli";

interface Props {
  rpc: CliRPC | null;
  onClose: () => void;
}

export function ProjectPicker({ rpc, onClose }: Props) {
  const agentId = selectedAgentId.value;
  const [step, setStep] = useState<"dirs" | "sessions">("dirs");
  const [loadingDirs, setLoadingDirs] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hoveredCwd, setHoveredCwd] = useState<string>("");

  const dirs: DirEntry[] = (agentId && dirsByAgent.value[agentId]) || [];
  const sessions: SessionEntry[] = (hoveredCwd && sessionsByCwd.value[hoveredCwd]) || [];

  async function loadDirs() {
    if (!agentId || !rpc) return;
    setLoadingDirs(true);
    setError(null);
    try {
      const r = await rpc.listDirs(agentId);
      dirsByAgent.value = { ...dirsByAgent.value, [agentId]: r.dirs };
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoadingDirs(false);
    }
  }

  async function loadSessions(cwd: string) {
    if (!agentId || !rpc) return;
    setLoadingSessions(true);
    try {
      const r = await rpc.listSessions(agentId, cwd);
      sessionsByCwd.value = { ...sessionsByCwd.value, [cwd]: r.sessions };
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoadingSessions(false);
    }
  }

  useEffect(() => {
    if (dirs.length === 0) void loadDirs();
  }, [agentId]);

  function pickDir(d: DirEntry) {
    setHoveredCwd(d.path);
    void loadSessions(d.path);
    setStep("sessions");
  }

  function confirmAsNew() {
    selectedCwd.value = hoveredCwd;
    selectedResumeId.value = "";
    continueRecent.value = false;
    onClose();
  }

  function confirmContinueRecent() {
    selectedCwd.value = hoveredCwd;
    selectedResumeId.value = "";
    continueRecent.value = true;
    onClose();
  }

  function confirmResume(s: SessionEntry) {
    selectedCwd.value = hoveredCwd;
    selectedResumeId.value = s.id;
    continueRecent.value = false;
    onClose();
  }

  function clearCwd() {
    selectedCwd.value = "";
    selectedResumeId.value = "";
    continueRecent.value = false;
    onClose();
  }

  return (
    <div class="picker-mask" onClick={onClose}>
      <aside class="picker" onClick={(e) => e.stopPropagation()}>
        <header class="picker-head">
          <button class="link-btn" onClick={onClose}>取消</button>
          <h2>{step === "dirs" ? "选择项目目录" : "续接？"}</h2>
          <button class="link-btn" onClick={clearCwd}>清空</button>
        </header>

        {step === "dirs" && (
          <div class="picker-body">
            {loadingDirs && <div class="muted">加载目录…</div>}
            {error && <div class="alert alert-err">{error}</div>}
            {!loadingDirs && dirs.length === 0 && (
              <div class="muted">
                <p>该 PC 上没有配置任何项目目录。</p>
                <p>在 PC 上修改 <code>C:\ear_to_cc\agent\start-agent.ps1</code>，
                设置 <code>$env:EAR_CLAUDE_PROJECT_ROOTS = "F:\projects;F:\code"</code>，
                然后重启 ear-cc-agent 计划任务。</p>
              </div>
            )}
            <ul class="dir-list">
              {dirs.map((d) => (
                <li key={d.path} class="dir-row" onClick={() => pickDir(d)}>
                  <div class="dir-row__top">
                    <span class="dir-name">{d.name}</span>
                    <span class="dir-meta">
                      {d.has_git && <span class="badge">git</span>}
                      {d.has_claude_md && <span class="badge badge-claude">CLAUDE.md</span>}
                      {d.session_count > 0 && (
                        <span class="badge badge-recent">{d.session_count} sess</span>
                      )}
                    </span>
                  </div>
                  <div class="dir-path">{d.path}</div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {step === "sessions" && (
          <div class="picker-body">
            <button class="link-btn" onClick={() => setStep("dirs")}>← 选别的目录</button>
            <div class="picked-cwd">📁 {hoveredCwd}</div>

            <div class="resume-options">
              <button class="resume-btn primary" onClick={confirmAsNew}>
                <strong>新对话</strong>
                <small>不读历史，全新 session</small>
              </button>
              <button class="resume-btn" onClick={confirmContinueRecent}>
                <strong>继续最近一次</strong>
                <small>用 claude -c，自动续接 cwd 下最新 session</small>
              </button>
            </div>

            <h3 class="sessions-title">已有 session（点选续接）</h3>
            {loadingSessions && <div class="muted">加载会话…</div>}
            {!loadingSessions && sessions.length === 0 && (
              <div class="muted small">该目录还没有 claude 会话历史</div>
            )}
            <ul class="session-list">
              {sessions.map((s) => (
                <li key={s.id} class="session-row" onClick={() => confirmResume(s)}>
                  <div class="session-row__top">
                    <span class="session-title">{s.ai_title || "(无标题)"}</span>
                    <span class="session-meta">{s.message_count} 条</span>
                  </div>
                  <div class="session-row__bottom">
                    <span class="session-time">
                      {s.updated_ts > 0
                        ? formatTimeAgo(s.updated_ts)
                        : ""}
                    </span>
                    <span class="session-id">{s.id.slice(0, 8)}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </aside>
    </div>
  );
}

function formatTimeAgo(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return "刚刚";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)} 分钟前`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)} 小时前`;
  return `${Math.floor(diff / 86400_000)} 天前`;
}
